import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, InlineNotification, Select, SelectItem, Tag, Toggle } from '@carbon/react';
import { formatVnd } from '@ruby-cms/shared';
import { api } from '../../api/client';
import { BlockSkeleton, ErrorState, InlineError } from '../../components/states';
import { HE_A_ERRORS } from '../../components/labels';
import { MoneyInput } from '../../components/MoneyInput';
import type { Command } from '../Commands';

// Quy bot poker (owner 2026-10-04): bot Texas / Omaha chi mua chip bang quy nay. Chi chu so huu thay tab nay
// (API tu choi moi role khac). Bat / tat, von (mac dinh tu config), so du, lai lo tu luc lap quy va so quy.

type CauHinh = { bat: boolean; von: number; nguon: 'cms' | 'mac_dinh'; phien_ban: number; mac_dinh: { bat?: boolean; von?: number } };
type Quy = { co: boolean; so_du: number; von: number; trenBan: number; lai: number; luc: string | null; khop: boolean; tongSo: number };
type DongSo = { id: number; loai: string; tien: number; so_du_sau: number; gid: string | null; ban: string | null; user_id: number | null; ghi_chu: string | null; luc: string };
type Data = {
  cauHinh: CauHinh | null; quy: Quy; tong: Record<string, { so: number; tien: number }>;
  so: { items: DongSo[]; total: number; page: number; pageSize: number }; loi?: string;
};

export const LOAI_SO: Record<string, string> = {
  von: 'Vốn', mua: 'Bot mua chip', mua_them: 'Bot mua thêm', rut: 'Bot đổi chip', hoan: 'Hoàn chip bot', phe: 'Phế',
};
const TEN_GAME: Record<string, string> = { vgcg_6: 'Texas', vgcg_12: 'Omaha' };
const PAGE_SIZE = 50;

export function PokerQuyBotTab() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [loai, setLoai] = useState('');
  const q = useQuery({
    queryKey: ['poker-quy-bot', page, loai],
    queryFn: () => api.get<Data>(`/api/admin/games/poker/quy-bot?page=${page}&pageSize=${PAGE_SIZE}${loai ? '&loai=' + loai : ''}`),
    refetchInterval: 5000,
  });
  const [form, setForm] = useState<{ bat: boolean; von: string } | null>(null);
  const [xong, setXong] = useState<Command | null>(null);
  const luu = useMutation({
    mutationFn: (f: { bat: boolean; von: number }) => api.put<{ command: Command }>('/api/admin/games/poker/quy-bot', f),
    onSuccess: (r) => { setXong(r.command); setForm(null); qc.invalidateQueries({ queryKey: ['poker-quy-bot'] }); },
  });

  if (q.isLoading) return <BlockSkeleton lines={8} />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const d = q.data;
  const ch = d.cauHinh;
  const f = form ?? (ch ? { bat: ch.bat, von: String(ch.von) } : null);
  const doi = !!f && !!ch && (f.bat !== ch.bat || Number(f.von) !== ch.von);
  const soTrang = Math.max(1, Math.ceil(d.so.total / PAGE_SIZE));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginTop: '1rem' }}>
      {d.loi ? <InlineNotification kind="warning" lowContrast hideCloseButton title="Kết nối máy game" subtitle="Hệ A chưa trả lời: chưa đổi được cài đặt." /> : null}
      {xong ? <KetQuaLenh cmd={xong} onClose={() => setXong(null)} /> : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
        <O ten="Số dư quỹ" gia={d.quy.co ? formatVnd(d.quy.so_du) : 'Chưa lập'} />
        <O ten="Chip bot đang trên bàn" gia={formatVnd(d.quy.trenBan)} />
        <O ten="Vốn" gia={d.quy.co ? formatVnd(d.quy.von) : '-'} />
        <O ten="Lãi / lỗ từ khi lập quỹ (gồm chip trên bàn)" gia={d.quy.co ? formatVnd(d.quy.lai) : '-'} mau={d.quy.lai < 0 ? 'var(--cds-support-error)' : undefined} />
        <O ten="Phế đã vào quỹ" gia={formatVnd(d.tong.phe?.tien ?? 0)} />
        <O ten="Lập quỹ lúc" gia={d.quy.luc ? new Date(d.quy.luc).toLocaleString('vi-VN') : '-'} />
      </div>
      {d.quy.co && ch && ch.von !== d.quy.von ? (
        <InlineNotification kind="info" lowContrast hideCloseButton title="Vốn mới đang chờ áp dụng"
          subtitle={`Vốn đặt ${formatVnd(ch.von)}, quỹ đang theo ${formatVnd(d.quy.von)}: giảm vốn chỉ áp khi số dư tiền mặt đủ (chip bot rời bàn về quỹ). Trong lúc chờ không bot mới nào ngồi.`} />
      ) : null}
      {d.quy.co && !d.quy.khop ? (
        <InlineNotification kind="error" lowContrast hideCloseButton title="Sổ quỹ lệch"
          subtitle={`Số dư ${formatVnd(d.quy.so_du)} khác tổng sổ ${formatVnd(d.quy.tongSo)}.`} />
      ) : null}

      <form aria-label="Cài đặt quỹ bot" style={{ padding: '1.25rem', border: '1px solid var(--cds-border-subtle)', borderRadius: '6px' }}
        onSubmit={(e) => { e.preventDefault(); if (f && doi) luu.mutate({ bat: f.bat, von: Number(f.von) }); }}>
        <h3 style={{ margin: '0 0 0.25rem', fontSize: '1.125rem' }}>Cài đặt</h3>
        <p className="muted" style={{ margin: '0 0 1rem' }}>
          Bot chỉ mua chip bằng quỹ này: vốn + chip bot đổi ra + phế poker - chip bot mua vào. Quỹ không đủ mức mua của bot ở một mức cược
          thì không bot nào ngồi mức đó, bot đang ngồi chơi hết ván rồi rời bàn. Lỗ của nhà ở poker từ khi lập quỹ không vượt quá vốn.
          Đổi vốn: số dư đổi đúng phần chênh.
        </p>
        {!ch ? <p className="muted">Chưa đọc được cài đặt.</p> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem', alignItems: 'start' }}>
            <Toggle id="qb-bat" labelText="Bot chơi bằng quỹ" labelA="Tắt" labelB="Bật" toggled={f!.bat}
              onToggle={(v: boolean) => setForm({ ...f!, bat: v })} />
            <MoneyInput id="qb-von" labelText="Vốn quỹ" className="money-input"
              helperText={`Mặc định ${formatVnd(Number(ch.mac_dinh.von ?? 0))}${ch.nguon === 'mac_dinh' ? ' (đang dùng)' : ''}`}
              value={f!.von} onChange={(v) => setForm({ ...f!, von: v })} />
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1rem' }}>
          <Button type="submit" size="md" disabled={!doi || luu.isPending || !!d.loi || f?.von === ''}>{luu.isPending ? 'Đang lưu...' : 'Lưu'}</Button>
          {ch ? <Tag type={ch.bat ? 'green' : 'gray'}>{ch.bat ? 'Đang bật' : 'Đang tắt'}</Tag> : null}
        </div>
        {luu.error ? <InlineError error={luu.error} /> : null}
      </form>

      <div style={{ padding: '1.25rem', border: '1px solid var(--cds-border-subtle)', borderRadius: '6px' }}>
        <div style={{ display: 'flex', alignItems: 'end', justifyContent: 'space-between', gap: '1rem', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.125rem' }}>Sổ quỹ ({d.so.total})</h3>
          <div style={{ width: '14rem' }}>
            <Select id="qb-loai" labelText="Loại" size="sm" value={loai} onChange={(e) => { setLoai(e.target.value); setPage(1); }}>
              <SelectItem value="" text="Tất cả" />
              {Object.entries(LOAI_SO).map(([k, v]) => <SelectItem key={k} value={k} text={v} />)}
            </Select>
          </div>
        </div>
        {d.so.items.length === 0 ? <p className="muted">Chưa có dòng nào.</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table aria-label="Sổ quỹ bot" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--cds-border-subtle)', textAlign: 'left' }}>
                  <th style={{ padding: '0.5rem' }}>#</th>
                  <th style={{ padding: '0.5rem' }}>Lúc</th>
                  <th style={{ padding: '0.5rem' }}>Loại</th>
                  <th style={{ padding: '0.5rem' }}>Game / bàn</th>
                  <th style={{ padding: '0.5rem' }}>Bot</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Tiền</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Số dư sau</th>
                  <th style={{ padding: '0.5rem' }}>Ghi chú</th>
                </tr>
              </thead>
              <tbody>
                {d.so.items.map((r) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid var(--cds-border-subtle)' }}>
                    <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>{r.id}</td>
                    <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>{r.luc}</td>
                    <td style={{ padding: '0.5rem' }}>{LOAI_SO[r.loai] ?? r.loai}</td>
                    <td style={{ padding: '0.5rem' }}>{r.gid ? (TEN_GAME[r.gid] ?? r.gid) : ''}{r.ban ? ' · ' + r.ban : ''}</td>
                    <td style={{ padding: '0.5rem' }}>{r.user_id ?? ''}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', color: r.tien < 0 ? 'var(--cds-support-error)' : undefined }}>{formatVnd(r.tien)}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatVnd(r.so_du_sau)}</td>
                    <td style={{ padding: '0.5rem' }} className="muted">{r.ghi_chu ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {soTrang > 1 ? (
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '1rem' }}>
            <Button size="sm" kind="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>Trước</Button>
            <span className="muted">Trang {page} / {soTrang}</span>
            <Button size="sm" kind="ghost" disabled={page >= soTrang} onClick={() => setPage(page + 1)}>Sau</Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function O({ ten, gia, mau }: { ten: string; gia: string; mau?: string }) {
  return (
    <div style={{ padding: '1rem', border: '1px solid var(--cds-border-subtle)', borderRadius: '6px' }}>
      <div className="muted" style={{ fontSize: '0.8125rem' }}>{ten}</div>
      <div style={{ fontSize: '1.25rem', fontWeight: 600, color: mau }}>{gia}</div>
    </div>
  );
}

function KetQuaLenh({ cmd, onClose }: { cmd: Command; onClose: () => void }) {
  const ok = cmd.status === 'SUCCEEDED';
  const code = cmd.lastError || (cmd.result && typeof cmd.result.loi === 'string' ? cmd.result.loi : '');
  return (
    <div>
      <InlineNotification kind={ok ? 'success' : 'warning'} lowContrast onClose={() => { onClose(); return false; }}
        title={ok ? 'Lưu quỹ bot: xong' : 'Lưu quỹ bot: lệnh chưa xong'}
        subtitle={ok ? 'Máy game áp dụng ngay.' : (HE_A_ERRORS[code] ?? (code || cmd.status))} />
      <p><Link className="cell-link" to={'/commands/' + cmd.id}>Xem lệnh</Link></p>
    </div>
  );
}
